// The owner of every recovery draft this tab is writing: a questionnaire editor's private copy, kept on
// the server apart from the task itself so the work survives the session ending.
//
// It lives in memory, outside any component, so closing an editor does not lose an edit still waiting
// to be saved. Nothing here ever touches browser storage, and draft content never travels in a Redux
// action: requests go to their saga workers through the transport (`questionnaire_draft_request.ts`),
// so neither the logger nor DevTools ever sees a draft.
//
// One slot per draft key, within the session scope that wrote it:
// - an editor writes through a writer; a later editor on the same key (the project's single slot
//   for new questionnaires, most often) replaces it, and the earlier one's calls are ignored
// - nothing an editor holds is saved until its gate opens, once any earlier copy has been dealt with;
//   the content it opens on counts as already saved unless the editor restored it, and an edit an
//   earlier editor left unsaved goes on being saved meanwhile
// - one save is out at a time, at most one every `DRAFT_SAVE_INTERVAL_MS`: the first edit after a quiet
//   interval goes at once, later ones go together at the end of it, as the newest copy
// - a reply acknowledges exactly the copy it carried
// - network failures and 5xx try again at the next interval; a 429 waits for its Retry-After; any other
//   refusal stops the slot until the researcher asks to try again
// - discarding the copy waits for a save already out, then deletes it; no save goes until the delete
//   is confirmed, so a late save cannot bring it back, and a delete the network lost is tried again
// - the session scope ending drops every slot of that scope and aborts what is out, so no copy is sent
//   with a later session's token; a sign-out first makes one last attempt with the session's own
//
// The real save follows its own ticket (`beginSave`, or a staged task's at publish). The copy is deleted
// under its original key only once every stage of that save -- content, display rules and the
// schedules it needs -- is confirmed, and only if nothing was edited after it was submitted. A save
// that stops part-way leaves the copy with a checkpoint of what it confirmed, and a create that was sent
// leaves a marker, so a later restore asks for saved work to be looked at rather than create again.
// The marker is a save of the copy like any other and can land after the create, or not at all; the
// create also carries the copy's create key, which the server refuses a second time.
import type {
  QuestionnaireDraftCheckpoint,
  QuestionnaireDraftKey,
  QuestionnaireDraftSavedBase,
  QuestionnaireDraftSaveStage,
  QuestionnaireDraftSnapshot,
  QuestionnaireDraftStagedSave,
  QuestionnaireDraftSubmissionMarker,
} from "../../model/questionnaire_draft";
import type { ITask } from "../../shared/db";
import {
  SessionScope,
  currentSessionScope,
  isSessionScopeCurrent,
  onSessionScopeEnd,
} from "../auth/session_scope";
import { hashString } from "./hash";

export const DRAFT_SAVE_INTERVAL_MS = 5000;

// the backend's cap on a serialized draft; a larger one is refused, so it is never sent
export const QUESTIONNAIRE_DRAFT_MAX_BYTES = 1048576;

export type DraftUpsertOutcome =
  | { kind: "saved"; updatedAt: string }
  // a network failure, a timeout or a 5xx
  | { kind: "retry" }
  | { kind: "rate_limited"; retryAfterMs: number | null }
  // any other 4xx but 401: this draft will not be accepted as it is
  | { kind: "refused" }
  // the session it was sent for is over in this tab
  | { kind: "stale" };

export type DraftSaveStatus =
  | { state: "idle" }
  | { state: "saving" }
  | { state: "saved"; updatedAt: string }
  | { state: "retrying" }
  | { state: "not_saving"; reason: "refused" | "too_large" }
  // the copy is being deleted; `uncertain` once an attempt went unanswered and is being tried again
  | { state: "removing"; uncertain: boolean }
  // the server refused to delete it
  | { state: "removal_refused" };

export interface QuestionnaireDraftUpsertRequest {
  scope: SessionScope;
  key: QuestionnaireDraftKey;
  draft: QuestionnaireDraftSnapshot;
  signal: AbortSignal;
}

export type DraftFetchOutcome =
  | { kind: "found"; draft: unknown; updatedAt: string }
  | { kind: "absent" }
  | { kind: "retry" }
  | { kind: "refused" }
  | { kind: "stale" };

export type DraftDeleteOutcome =
  | { kind: "deleted" }
  | { kind: "retry" }
  | { kind: "refused" }
  | { kind: "stale" };

export type SavedQuestionnaireTasksOutcome =
  | { kind: "ok"; taskList: ITask[] }
  | { kind: "retry" }
  | { kind: "refused" }
  | { kind: "stale" };

export interface QuestionnaireDraftScopedRequest {
  scope: SessionScope;
  key: QuestionnaireDraftKey;
}

export interface QuestionnaireDraftTransport {
  // answers how the save went; a request whose scope is over answers `stale`
  upsert: (request: QuestionnaireDraftUpsertRequest) => Promise<DraftUpsertOutcome>;
  // one last attempt at sign-out, with the session's token as it is at that moment; never awaited
  finalUpsert: (request: { key: QuestionnaireDraftKey; draft: QuestionnaireDraftSnapshot }) => void;
  fetch: (request: QuestionnaireDraftScopedRequest) => Promise<DraftFetchOutcome>;
  delete: (request: QuestionnaireDraftScopedRequest) => Promise<DraftDeleteOutcome>;
  // the method's saved tasks as they are now, which a copy is judged against before it is offered
  fetchSavedTasks: (request: {
    scope: SessionScope;
    methodId: string;
  }) => Promise<SavedQuestionnaireTasksOutcome>;
}

export interface QuestionnaireDraftWriter {
  // The earlier copy has been dealt with: edits from here on are saved. `restored`: the editor now
  // holds a restored copy, which is saved again rather than taken as already saved.
  openGate: (options?: { restored?: boolean }) => void;
  noteChange: () => void;
  // the editor is closing: its last edit is taken now, and saving it goes on without the editor
  close: () => void;
  // try again after a refusal stopped saving
  retry: () => void;
  // The researcher discarded the copy. The gate opens on what the editor holds now, and nothing is
  // saved until the server's copy is gone.
  discard: () => void;
  // an earlier editor's copy this tab holds and the server may not have yet
  heldCopy: () => QuestionnaireDraftSnapshot | null;
  // The real save is being sent: its ticket, for the save path to report each stage it confirms. A
  // create first marks the copy as submitted.
  beginSave: (options: { createsTask: boolean; requiresSchedules: boolean }) => string | null;
  // the edit is staged for a live project's publish, which saves it later
  markStaged: () => QuestionnaireDraftStagedSave | null;
  getStatus: () => DraftSaveStatus;
  subscribe: (listener: () => void) => () => void;
}

interface Writer {
  id: number;
  read: () => QuestionnaireDraftSnapshot | null;
  gateOpen: boolean;
}

interface Slot {
  id: string;
  // unique across the tab's lifetime, so a save can tell its slot from one made later for the same key
  instance: number;
  scope: SessionScope;
  key: QuestionnaireDraftKey;
  writer: Writer | null;
  // the coordinator's own count of distinct copies read for this slot, across editors
  revision: number;
  lastRead: { writerId: number; editorRevision: number } | null;
  latest: QuestionnaireDraftSnapshot | null;
  // the editor reported a change not read yet
  dirty: boolean;
  // copies at or below this revision need no save: acknowledged, or what the gate opened on
  savedRevision: number;
  acknowledgedAt: string | null;
  inFlight: { revision: number; controller: AbortController } | null;
  lastSentAt: number | null;
  retryNotBefore: number | null;
  retrying: boolean;
  stopped: "refused" | "too_large" | null;
  // the copy was discarded: no save goes until the server's is gone
  removal: { attempt: number; refused: boolean } | null;
  // what the real save left on the copy; `undefined` leaves what the editor holds as it is
  submission: QuestionnaireDraftSubmissionMarker | null | undefined;
  checkpoint: QuestionnaireDraftCheckpoint | undefined;
  // the copy's content is saved, but what the real save left on it is not yet
  resend: boolean;
  // a live project's staged task was made from this copy and is not published yet, so the copy is kept
  // in memory for the publish to record its create on
  staged: boolean;
  timer: ReturnType<typeof setTimeout> | null;
  listeners: Set<() => void>;
  status: DraftSaveStatus;
}

export const IDLE_DRAFT_STATUS: DraftSaveStatus = { state: "idle" };

let transport: QuestionnaireDraftTransport | null = null;
const slots = new Map<string, Slot>();
let writerCount = 0;
let slotCount = 0;

interface SaveTicket {
  scope: SessionScope;
  key: QuestionnaireDraftKey;
  // the slot and revision the save was taken from; null when this tab held no copy then
  slotInstance: number | null;
  revision: number;
  createsTask: boolean;
  required: QuestionnaireDraftSaveStage[];
  confirmed: Set<QuestionnaireDraftSaveStage>;
  checkpoint: { taskId: string; base: QuestionnaireDraftSavedBase } | null;
}

const saveTickets = new Map<string, SaveTicket>();
let saveTicketCount = 0;

export const configureQuestionnaireDraftTransport = (next: QuestionnaireDraftTransport): void => {
  transport = next;
};

// The size the backend measures: the UTF-8 bytes of the serialized draft
export const utf8ByteLength = (text: string): number => new TextEncoder().encode(text).length;

// Every UTF-16 unit is one to three UTF-8 bytes, so most drafts are settled without encoding them
const exceedsDraftLimit = (draft: QuestionnaireDraftSnapshot): boolean => {
  const json = JSON.stringify(draft);
  if (json.length > QUESTIONNAIRE_DRAFT_MAX_BYTES) return true;
  if (json.length * 3 <= QUESTIONNAIRE_DRAFT_MAX_BYTES) return false;
  return utf8ByteLength(json) > QUESTIONNAIRE_DRAFT_MAX_BYTES;
};

const slotIdOf = (scope: SessionScope, key: QuestionnaireDraftKey) =>
  `${scope.generation}|${key.projectId}|${key.taskId ?? "new"}`;

// a copy, or what the real save left on it, the server does not have yet
const hasUnsent = (slot: Slot) => slot.revision > slot.savedRevision || slot.resend;
const hasUnsaved = (slot: Slot) => hasUnsent(slot) || (slot.dirty && !!slot.writer?.gateOpen);

// the copy as it goes to the server, with what the real save left on it
const decorated = (slot: Slot, draft: QuestionnaireDraftSnapshot): QuestionnaireDraftSnapshot => {
  if (slot.submission === undefined && slot.checkpoint === undefined) return draft;
  return {
    ...draft,
    base: {
      ...draft.base,
      ...(slot.submission === undefined ? {} : { submission: slot.submission }),
      ...(slot.checkpoint === undefined ? {} : { checkpoint: slot.checkpoint }),
    },
  };
};

const statusOf = (slot: Slot): DraftSaveStatus => {
  if (slot.removal) {
    return slot.removal.refused
      ? { state: "removal_refused" }
      : { state: "removing", uncertain: slot.removal.attempt > 1 };
  }
  if (slot.stopped) return { state: "not_saving", reason: slot.stopped };
  if (slot.inFlight || hasUnsaved(slot)) {
    return slot.retrying ? { state: "retrying" } : { state: "saving" };
  }
  if (slot.acknowledgedAt) return { state: "saved", updatedAt: slot.acknowledgedAt };
  return IDLE_DRAFT_STATUS;
};

const sameStatus = (a: DraftSaveStatus, b: DraftSaveStatus) =>
  JSON.stringify(a) === JSON.stringify(b);

const notify = (slot: Slot) => {
  const status = statusOf(slot);
  if (sameStatus(status, slot.status)) return;
  slot.status = status;
  slot.listeners.forEach((listener) => listener());
};

// Reads the editor's current copy, if it has one, and counts it as a new revision when it changed
const takeLatest = (slot: Slot) => {
  const writer = slot.writer;
  if (!writer?.gateOpen) return;
  slot.dirty = false;
  const snapshot = writer.read();
  if (!snapshot) return;
  const read = { writerId: writer.id, editorRevision: snapshot.revision };
  if (
    slot.lastRead &&
    slot.lastRead.writerId === read.writerId &&
    slot.lastRead.editorRevision === read.editorRevision
  ) {
    return;
  }
  slot.lastRead = read;
  slot.revision += 1;
  slot.latest = snapshot;
};

const clearTimer = (slot: Slot) => {
  if (slot.timer) clearTimeout(slot.timer);
  slot.timer = null;
};

const schedule = (slot: Slot, waitMs: number) => {
  clearTimer(slot);
  slot.timer = setTimeout(() => {
    slot.timer = null;
    tick(slot);
  }, waitMs);
};

const forget = (slot: Slot) => {
  if (
    slot.writer ||
    slot.inFlight ||
    slot.stopped ||
    slot.removal ||
    slot.staged ||
    hasUnsaved(slot)
  )
    return;
  clearTimer(slot);
  slots.delete(slot.id);
};

const send = (slot: Slot) => {
  const draft = slot.latest && decorated(slot, slot.latest);
  if (!draft || !transport) return;
  // measured again after a later edit, no sooner than the interval: it may be back under the cap
  if (exceedsDraftLimit(draft)) {
    slot.stopped = "too_large";
    slot.lastSentAt = Date.now();
    notify(slot);
    return;
  }
  if (slot.stopped === "too_large") slot.stopped = null;

  const controller = new AbortController();
  const flight = { revision: slot.revision, controller };
  // this save takes what the real save left on the copy; anything it records while the save is out
  // goes in the next one
  const carriesResend = slot.resend;
  slot.resend = false;
  slot.inFlight = flight;
  slot.lastSentAt = Date.now();
  notify(slot);

  void transport
    .upsert({ scope: slot.scope, key: slot.key, draft, signal: controller.signal })
    .then((outcome) => {
      // dropped, or replaced by a later save, meanwhile
      if (slots.get(slot.id) !== slot || slot.inFlight !== flight) return;
      slot.inFlight = null;
      switch (outcome.kind) {
        case "saved":
          slot.savedRevision = Math.max(slot.savedRevision, flight.revision);
          slot.acknowledgedAt = outcome.updatedAt;
          slot.retrying = false;
          break;
        case "retry":
          slot.retrying = true;
          break;
        case "rate_limited":
          slot.retrying = true;
          slot.retryNotBefore = Date.now() + (outcome.retryAfterMs ?? DRAFT_SAVE_INTERVAL_MS);
          break;
        case "refused":
          slot.stopped = "refused";
          break;
        case "stale":
          break;
      }
      // not saved: what it carried goes again, unless the copy is being discarded
      if (outcome.kind !== "saved" && carriesResend && !slot.removal) slot.resend = true;
      notify(slot);
      // a discard waiting behind this save goes now
      if (slot.removal) remove(slot);
      else tick(slot);
      forget(slot);
    });
};

const remove = (slot: Slot) => {
  const removal = slot.removal;
  if (!removal || removal.refused || slot.inFlight || !transport || slots.get(slot.id) !== slot)
    return;
  if (!isSessionScopeCurrent(slot.scope)) return;
  clearTimer(slot);
  removal.attempt += 1;
  notify(slot);
  void transport.delete({ scope: slot.scope, key: slot.key }).then((outcome) => {
    if (slots.get(slot.id) !== slot || slot.removal !== removal) return;
    switch (outcome.kind) {
      case "deleted":
        slot.removal = null;
        slot.acknowledgedAt = null;
        notify(slot);
        // edits made while it was being deleted are saved now, as a new copy
        tick(slot);
        forget(slot);
        return;
      case "retry":
        // the delete may or may not have landed; it is safe to send again
        slot.timer = setTimeout(() => {
          slot.timer = null;
          remove(slot);
        }, DRAFT_SAVE_INTERVAL_MS);
        notify(slot);
        return;
      case "refused":
        removal.refused = true;
        notify(slot);
        return;
      case "stale":
        return;
    }
  });
};

// `now`: a session warning, which does not wait for the interval
function tick(slot: Slot, { now = false }: { now?: boolean } = {}) {
  if (slots.get(slot.id) !== slot || slot.stopped === "refused" || slot.removal || slot.inFlight)
    return;
  if (!isSessionScopeCurrent(slot.scope)) return;
  const at = Date.now();
  if (slot.retryNotBefore !== null && at < slot.retryNotBefore) {
    schedule(slot, slot.retryNotBefore - at);
    return;
  }
  if (!now && slot.lastSentAt !== null && at - slot.lastSentAt < DRAFT_SAVE_INTERVAL_MS) {
    schedule(slot, slot.lastSentAt + DRAFT_SAVE_INTERVAL_MS - at);
    return;
  }
  clearTimer(slot);
  takeLatest(slot);
  if (hasUnsent(slot)) send(slot);
  notify(slot);
}

const slotFor = (scope: SessionScope, key: QuestionnaireDraftKey): Slot => {
  const id = slotIdOf(scope, key);
  const existing = slots.get(id);
  if (existing) return existing;
  const slot: Slot = {
    id,
    instance: (slotCount += 1),
    scope,
    key,
    writer: null,
    revision: 0,
    lastRead: null,
    latest: null,
    dirty: false,
    savedRevision: 0,
    acknowledgedAt: null,
    inFlight: null,
    lastSentAt: null,
    retryNotBefore: null,
    retrying: false,
    stopped: null,
    removal: null,
    submission: undefined,
    checkpoint: undefined,
    resend: false,
    staged: false,
    timer: null,
    listeners: new Set(),
    status: IDLE_DRAFT_STATUS,
  };
  slots.set(id, slot);
  return slot;
};

// Deletes the slot's copy, after a save already out for it. What the editor holds now counts as saved,
// and nothing the real save left on the copy goes out again, so nothing can bring the copy back.
const beginRemoval = (slot: Slot) => {
  if (slot.writer) slot.writer.gateOpen = true;
  takeLatest(slot);
  slot.savedRevision = slot.revision;
  slot.resend = false;
  slot.submission = undefined;
  slot.checkpoint = undefined;
  slot.stopped = null;
  slot.retrying = false;
  slot.retryNotBefore = null;
  slot.removal = { attempt: 0, refused: false };
  clearTimer(slot);
  notify(slot);
  remove(slot);
};

// What a real save left behind goes out with the copy: the task it saved, and the stages it confirmed
const recordCheckpoint = (
  slot: Slot,
  ticket: SaveTicket & { checkpoint: NonNullable<SaveTicket["checkpoint"]> },
) => {
  slot.checkpoint = { ...ticket.checkpoint, confirmedStageList: [...ticket.confirmed], idMap: {} };
  slot.submission = null;
};

const resendNow = (slot: Slot) => {
  slot.resend = true;
  tick(slot, { now: true });
  notify(slot);
};

const createSaveTicket = (
  ticket: Omit<SaveTicket, "required" | "confirmed" | "checkpoint">,
  requiresSchedules: boolean,
): string => {
  const id = `questionnaire-draft-save-${(saveTicketCount += 1)}`;
  // the model's stage values, spelt out: this module imports the model for its types only
  const required = ["content", "display_strategy", ...(requiresSchedules ? ["schedule"] : [])];
  saveTickets.set(id, {
    ...ticket,
    required: required as QuestionnaireDraftSaveStage[],
    confirmed: new Set(),
    checkpoint: null,
  });
  return id;
};

export const openQuestionnaireDraftWriter = ({
  key,
  read,
}: {
  key: QuestionnaireDraftKey;
  read: () => QuestionnaireDraftSnapshot | null;
}): QuestionnaireDraftWriter | null => {
  const scope = currentSessionScope();
  if (!scope) return null;

  const target = slotFor(scope, key);
  // what an earlier editor left that the server may not have yet
  // as it would be sent: a submitted mark or checkpoint still to go out is part of it
  const held =
    target.latest && (target.inFlight || hasUnsaved(target))
      ? decorated(target, target.latest)
      : null;
  const writer: Writer = { id: (writerCount += 1), read, gateOpen: false };
  target.writer = writer;
  target.dirty = false;

  const isCurrent = () => slots.get(target.id) === target && target.writer === writer;

  return {
    openGate: ({ restored = false } = {}) => {
      if (!isCurrent() || writer.gateOpen) return;
      writer.gateOpen = true;
      takeLatest(target);
      // a fresh start replaces whatever an earlier editor left unsaved
      if (!restored) target.savedRevision = target.revision;
      tick(target);
      notify(target);
    },
    noteChange: () => {
      if (!isCurrent()) return;
      target.dirty = true;
      tick(target);
      notify(target);
    },
    close: () => {
      if (!isCurrent()) return;
      takeLatest(target);
      target.writer = null;
      tick(target);
      notify(target);
      forget(target);
    },
    retry: () => {
      if (!isCurrent() || !target.stopped) return;
      target.stopped = null;
      target.retrying = false;
      tick(target, { now: true });
      notify(target);
    },
    discard: () => {
      if (isCurrent()) beginRemoval(target);
    },
    heldCopy: () => (isCurrent() ? held : null),
    beginSave: ({ createsTask, requiresSchedules }) => {
      if (!isCurrent()) return null;
      takeLatest(target);
      const ticket = createSaveTicket(
        {
          scope: target.scope,
          key: target.key,
          slotInstance: target.instance,
          revision: target.revision,
          createsTask,
        },
        requiresSchedules,
      );
      // Recorded in the copy before the create goes out, as far as a save can manage it, so a create
      // whose answer is lost is not quietly made again from a restored copy. It never holds the save up.
      if (createsTask && target.latest) {
        target.submission = {
          revision: target.revision,
          fingerprint: hashString(JSON.stringify(target.latest)),
        };
        resendNow(target);
      }
      return ticket;
    },
    markStaged: () => {
      if (!isCurrent()) return null;
      takeLatest(target);
      target.staged = true;
      return {
        nonce: target.scope.nonce,
        key: target.key,
        slotInstance: target.instance,
        revision: target.revision,
      };
    },
    getStatus: () => (slots.get(target.id) === target ? target.status : IDLE_DRAFT_STATUS),
    subscribe: (listener) => {
      target.listeners.add(listener);
      return () => {
        target.listeners.delete(listener);
      };
    },
  };
};

// A staged task being published: its save follows a ticket like a direct one, as long as the session
// that staged it is still the one signed in
export const openStagedQuestionnaireDraftSave = ({
  staged,
  createsTask,
  requiresSchedules,
}: {
  staged: QuestionnaireDraftStagedSave;
  createsTask: boolean;
  requiresSchedules: boolean;
}): string | null => {
  const scope = currentSessionScope();
  if (!scope || scope.nonce !== staged.nonce) return null;
  const ticket = createSaveTicket(
    {
      scope,
      key: staged.key,
      slotInstance: staged.slotInstance,
      revision: staged.revision,
      createsTask,
    },
    requiresSchedules,
  );
  // as an editor's own save does: the copy records the create before it goes out, while the copy is
  // still the one that was staged
  const slot = slots.get(slotIdOf(scope, staged.key));
  if (
    createsTask &&
    slot?.instance === staged.slotInstance &&
    slot.latest &&
    slot.revision === staged.revision
  ) {
    slot.submission = {
      revision: slot.revision,
      fingerprint: hashString(JSON.stringify(slot.latest)),
    };
    resendNow(slot);
  }
  return ticket;
};

// The session a save belongs to. A save path checks it is still current before its token wait, before
// each request and before it publishes, so a save made before a sign-out or a sign-in as someone else
// never goes out with the next session's token. Null once the ticket is settled or its session ended.
export const questionnaireDraftSaveScope = (ticketId: string): SessionScope | null =>
  saveTickets.get(ticketId)?.scope ?? null;

// The save annotated with `ticketId` still belongs to the session signed in; a save with no ticket
// is no recovery copy's business. Captured once at the start of the save, so a ticket that settles
// part-way does not stop its own last steps.
export const questionnaireDraftSaveOwnership = (ticketId: string | undefined): (() => boolean) => {
  if (!ticketId) return () => true;
  const scope = questionnaireDraftSaveScope(ticketId);
  return () => scope !== null && isSessionScopeCurrent(scope);
};

const settledTicket = (ticketId: string): SaveTicket | null => {
  const ticket = saveTickets.get(ticketId) ?? null;
  if (!ticket) return null;
  if (!isSessionScopeCurrent(ticket.scope)) {
    saveTickets.delete(ticketId);
    return null;
  }
  return ticket;
};

const completeSave = (ticket: SaveTicket) => {
  const slot = slots.get(slotIdOf(ticket.scope, ticket.key));
  if (slot) {
    slot.staged = false;
    takeLatest(slot);
    // Edited after the save was submitted, here or by a later editor: those edits stay, and since the
    // task they continue now exists, a restore of them looks at saved work rather than create again.
    const newerEdits =
      slot.instance !== ticket.slotInstance || slot.revision > ticket.revision || slot.dirty;
    if (newerEdits) {
      if (ticket.createsTask && ticket.checkpoint) {
        recordCheckpoint(slot, { ...ticket, checkpoint: ticket.checkpoint });
        resendNow(slot);
      }
      return;
    }
  }
  // the copy goes whether or not this tab still holds it
  beginRemoval(slot ?? slotFor(ticket.scope, ticket.key));
};

// One stage of the real save is confirmed. `checkpoint`: the saved task the content stage produced,
// kept should a later stage fail.
export const confirmQuestionnaireDraftSaveStage = (
  ticketId: string,
  stage: QuestionnaireDraftSaveStage,
  detail: { checkpoint?: { taskId: string; base: QuestionnaireDraftSavedBase } } = {},
): void => {
  const ticket = settledTicket(ticketId);
  if (!ticket) return;
  ticket.confirmed.add(stage);
  if (detail.checkpoint) ticket.checkpoint = detail.checkpoint;
  if (ticket.required.every((required) => ticket.confirmed.has(required))) {
    saveTickets.delete(ticketId);
    completeSave(ticket);
  }
};

// The real save stopped. `ambiguous`: no answer says whether its last request landed (a network failure
// or a 5xx), so a create that was sent may still have gone through. The copy stays, with a checkpoint of
// what was confirmed, so a restore resumes from there instead of creating the task again.
export const failQuestionnaireDraftSave = (
  ticketId: string,
  { ambiguous }: { ambiguous: boolean },
): void => {
  const ticket = settledTicket(ticketId);
  if (!ticket) return;
  saveTickets.delete(ticketId);
  const slot = slots.get(slotIdOf(ticket.scope, ticket.key));
  if (!slot) return;
  slot.staged = false;
  if (ticket.checkpoint) {
    recordCheckpoint(slot, { ...ticket, checkpoint: ticket.checkpoint });
  } else if (ambiguous) {
    forget(slot);
    return;
  } else {
    // refused outright: nothing was created, so a restore may create it again
    slot.submission = null;
  }
  resendNow(slot);
};

// The key of the copy a save finishes, while its ticket is open
export const questionnaireDraftSaveKey = (ticketId: string): QuestionnaireDraftKey | null =>
  saveTickets.get(ticketId)?.key ?? null;

// The researcher discarded a copy no editor has open. It goes as an editor's discard does: after a save
// already out for it, and with nothing the real save left on it sent again.
export const discardQuestionnaireDraft = (
  scope: SessionScope,
  key: QuestionnaireDraftKey,
): void => {
  if (isSessionScopeCurrent(scope)) beginRemoval(slotFor(scope, key));
};

// The identity's copy for `key` on the server, fetched for the session as it is now
export const fetchQuestionnaireDraft = async (
  key: QuestionnaireDraftKey,
): Promise<DraftFetchOutcome> => {
  const scope = currentSessionScope();
  if (!scope || !transport) return { kind: "stale" };
  return transport.fetch({ scope, key });
};

// The method's saved tasks, fetched for the session as it is now
export const fetchSavedQuestionnaireTasks = async (
  methodId: string,
): Promise<SavedQuestionnaireTasksOutcome> => {
  const scope = currentSessionScope();
  if (!scope || !transport) return { kind: "stale" };
  return transport.fetchSavedTasks({ scope, methodId });
};

// A session warning has appeared: save every open draft now rather than at the end of its interval
export const flushQuestionnaireDrafts = (): void => {
  slots.forEach((slot) => tick(slot, { now: true }));
};

onSessionScopeEnd((ended, { signingOut }) => {
  saveTickets.forEach((ticket, ticketId) => {
    if (ticket.scope.generation === ended.generation) saveTickets.delete(ticketId);
  });
  slots.forEach((slot) => {
    if (slot.scope.generation !== ended.generation) return;
    // Signing out: one last attempt for a copy not saved yet, with the token as it is now. Not beside
    // a save already out for the same key -- that one is the attempt, so it is left to finish on the
    // ending session's own token -- and never waited for. Its reply finds no slot and changes nothing.
    if (signingOut && transport && !slot.stopped && !slot.removal && !slot.inFlight) {
      takeLatest(slot);
      const draft = slot.latest && decorated(slot, slot.latest);
      if (draft && hasUnsent(slot) && !exceedsDraftLimit(draft))
        transport.finalUpsert({ key: slot.key, draft });
    }
    clearTimer(slot);
    if (!signingOut) slot.inFlight?.controller.abort();
    slot.inFlight = null;
    slot.writer = null;
    slot.latest = null;
    slots.delete(slot.id);
    slot.status = IDLE_DRAFT_STATUS;
    slot.listeners.forEach((listener) => listener());
  });
});
